<template>
  <div class="empty-state" :class="{ 'empty-state--restricted': restricted }">
    <div class="empty-state-icon">
      <i :class="iconClass"></i>
    </div>
    <div class="empty-state-title">{{ computedTitle }}</div>
    <div class="empty-state-text">{{ computedMessage }}</div>
  </div>
</template>

<script>
import {
  getRestrictedMessage,
  getPageLabel,
} from '@/utils/permissions';

export default {
  name: 'EmptyState',
  props: {
    // Generic copy (used when not restricted; keeps backward compatibility
    // with every existing call site that passes title/message/icon).
    title: {
      type: String,
      default: 'No Data Available'
    },
    message: {
      type: String,
      default: 'Data will appear once activity is recorded'
    },
    icon: {
      type: String,
      default: 'bar-chart'
    },

    // Restricted-mode props (additive — opt-in per call site).
    // When `restricted` is true, the component renders the lock icon, the
    // "Access Restricted" title, and a message naming the relevant
    // *_RESPONSIBLE_EMPLOYEEID setting via getRestrictedMessage(subject).
    // Callers pass the CASL subject (e.g. 'overdue_recall') and the
    // component looks up the label.
    restricted: {
      type: Boolean,
      default: false
    },
    subject: {
      type: String,
      default: ''
    },
    // Optional override for the restricted message. If provided, used
    // verbatim instead of the auto-generated copy. Lets callers in
    // non-follow-up contexts supply their own copy.
    restrictedMessage: {
      type: String,
      default: ''
    },
  },
  computed: {
    iconClass() {
      const icon = this.restricted ? 'lock' : this.icon;
      return `fa fa-${icon}`;
    },
    computedTitle() {
      if (!this.restricted) return this.title;
      // For follow-up subjects we have a known page label; otherwise show
      // the generic "Access Restricted" header.
      return 'Access Restricted';
    },
    computedMessage() {
      if (!this.restricted) return this.message;
      if (this.restrictedMessage) return this.restrictedMessage;
      if (this.subject) return getRestrictedMessage(this.subject);
      // Fallback if a caller flips `restricted` without giving any
      // context — better than rendering nothing.
      return 'Ask your admin to grant your role access.';
    },
  }
};
</script>

<style scoped>
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-state-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(30, 183, 145, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-state-icon i {
  font-size: 24px;
  color: #1eb791;
}

/* Restricted-mode color treatment. Distinct enough to scan, not alarming
   — this is informational, not an error. */
.empty-state--restricted .empty-state-icon {
  background-color: rgba(255, 159, 64, 0.12);
}

.empty-state--restricted .empty-state-icon i {
  color: #d6890c;
}

.empty-state-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.empty-state-text {
  font-size: 14px;
  color: #888;
  /* Restricted copy is longer ("Ask your admin to add you to the ..."); keep
     it readable without overflowing narrow cards. */
  max-width: 360px;
  line-height: 1.4;
}
</style>
